import { addTransitionType, startTransition, useLayoutEffect, useState } from "react";
import { Router, UNSAFE_createBrowserHistory } from "react-router";

import { NavigationTransitionTypesContext } from "@/navigation/hooks/use-arrived-with-transition-type";
import { resolveNavigationTransitionTypes } from "@/navigation/utils/navigation-transition-types";

import type { ReactNode } from "react";

/**
 * `BrowserRouter`, telling React what kind of navigation each one is.
 *
 * A view transition's class can answer to a transition type, and a type has to
 * be added inside the transition it describes. `BrowserRouter` starts that
 * transition itself and adds none, so this is the same router with the one
 * place that sees both ends of every navigation — a link, `navigate`, and the
 * browser's own back and forward — naming the types it carries.
 *
 * The types are also handed to the page the navigation opens, in the same
 * update as its location: a page a cover is travelling into has to know before
 * it first renders, because it must not be moving while the cover lands on it.
 */
export const TransitionTypeRouter = ({ children }: { children: ReactNode }) => {
  const [history] = useState(() => UNSAFE_createBrowserHistory({ v5Compat: true }));
  const [state, setState] = useState<{
    action: typeof history.action;
    location: typeof history.location;
    types: readonly string[];
  }>({ action: history.action, location: history.location, types: [] });

  useLayoutEffect(() => {
    let fromPathname = history.location.pathname;
    return history.listen((update) => {
      const types = resolveNavigationTransitionTypes({
        fromPathname,
        toPathname: update.location.pathname,
      });
      fromPathname = update.location.pathname;
      startTransition(() => {
        for (const type of types) {
          addTransitionType(type);
        }
        setState({ action: update.action, location: update.location, types });
      });
    });
  }, [history]);

  return (
    <NavigationTransitionTypesContext value={state.types}>
      <Router location={state.location} navigationType={state.action} navigator={history}>
        {children}
      </Router>
    </NavigationTransitionTypesContext>
  );
};
